import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { isUnderSettingsArea } from '../config/settingsNav';
import {
  NAV_GROUPS,
  buildNavItems,
  resolvePageTitle,
} from '../config/navConfig';
import { getCurrentUser, isInfluencerUser } from '../utils/auth';
import { productSettingsAPI } from '../services/api';

const Layout = ({ children, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [auctionsEnabled, setAuctionsEnabled] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const isInfluencer = isInfluencerUser();
  const currentUser = getCurrentUser();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await productSettingsAPI.get();
        if (!cancelled) setAuctionsEnabled(res.data?.auctions_enabled !== false);
      } catch {
        if (!cancelled) setAuctionsEnabled(true);
      }
    };
    load();
    const onChange = () => load();
    window.addEventListener('exglam-settings-changed', onChange);
    return () => {
      cancelled = true;
      window.removeEventListener('exglam-settings-changed', onChange);
    };
  }, []);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  const menuItems = useMemo(
    () => buildNavItems({ isInfluencer, auctionsEnabled }),
    [isInfluencer, auctionsEnabled],
  );

  const pathname = location.pathname;
  const headerTitle = resolvePageTitle(pathname, menuItems);
  const settingsNavActive = isUnderSettingsArea(pathname);

  const userLabel =
    [currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(' ') ||
    currentUser?.email ||
    'Admin';

  const userInitials = userLabel
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  const isActive = (item) =>
    pathname === item.path ||
    (item.path !== '/' && pathname.startsWith(`${item.path}/`)) ||
    (pathname === '/' && item.id === 'dashboard');

  return (
    <div className={`app-container ${mobileNavOpen ? 'nav-open' : ''}`}>
      {mobileNavOpen ? (
        <button
          type="button"
          className="sidebar-backdrop"
          aria-label="Close menu"
          onClick={() => setMobileNavOpen(false)}
        />
      ) : null}

      <aside className="sidebar" aria-label="Main navigation">
        <div className="sidebar-scroll">
          <div className="sidebar-header">
            <div className="sidebar-brand-mark" aria-hidden>
              EX
            </div>
            <div>
              <h1>ExGlam</h1>
              <p>Administration</p>
            </div>
          </div>

          {NAV_GROUPS.map((group) => {
            const items = menuItems.filter((item) => item.group === group.key);
            if (items.length === 0) return null;

            return (
              <div className="sidebar-group" key={group.key}>
                <p className="sidebar-group-title">{group.label}</p>
                <ul className="sidebar-menu">
                  {items.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={item.path}
                        className={`menu-item ${isActive(item) ? 'active' : ''}`}
                      >
                        <span className="menu-item-icon" aria-hidden>
                          {item.icon}
                        </span>
                        <span className="menu-item-label">{item.label}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="sidebar-footer">
          {!isInfluencer ? (
            <Link
              to="/settings"
              className={`sidebar-settings-btn ${settingsNavActive ? 'active' : ''}`}
            >
              <span className="sidebar-settings-icon" aria-hidden>
                ⚙
              </span>
              <span>Settings</span>
            </Link>
          ) : null}

          <div className="sidebar-user">
            <div className="sidebar-user-avatar" aria-hidden>
              {userInitials}
            </div>
            <div className="sidebar-user-info">
              <strong>{userLabel}</strong>
              {currentUser?.email && userLabel !== currentUser.email ? (
                <span>{currentUser.email}</span>
              ) : null}
            </div>
          </div>

          <button type="button" className="sidebar-signout-btn" onClick={handleLogout}>
            Sign out
          </button>
        </div>
      </aside>

      <div className="main-content">
        <header className="content-header">
          <div className="content-header-start">
            <button
              type="button"
              className="mobile-nav-toggle"
              aria-label="Open menu"
              aria-expanded={mobileNavOpen}
              onClick={() => setMobileNavOpen((v) => !v)}
            >
              ☰
            </button>
            <div>
              <h2>{headerTitle}</h2>
              <p className="content-header-meta">
                {isInfluencer ? 'Closet dashboard' : 'Manage your storefront'}
              </p>
            </div>
          </div>
        </header>
        <main className="content-area">{children}</main>
      </div>
    </div>
  );
};

export default Layout;
